import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from './logger.service';

/**
 * Servicio para gestionar la validación de PIN durante el refresh token
 * Proporciona un mecanismo de comunicación entre el interceptor y el modal de validación
 */
@Injectable({
  providedIn: 'root'
})
export class PinValidationService {
  
  private showPinModalSubject = new BehaviorSubject<boolean>(false);
  public showPinModal$ = this.showPinModalSubject.asObservable();
  
  private pinValidatedSubject = new BehaviorSubject<boolean>(false);
  public pinValidated$ = this.pinValidatedSubject.asObservable();
  
  private readonly FAILED_ATTEMPTS_KEY = 'pinValidationFailedAttempts';
  
  private failedAttempts = 0;
  private readonly MAX_ATTEMPTS = 3;
  
  private pinSentInCurrentSession = false;
  
  private logger: LoggerService;

  constructor() {
    this.logger = new LoggerService();
    this.loadFailedAttempts();
    
    // Sincronización entre pestañas
    window.addEventListener('storage', (event) => {
      if (event.key === 'pinValidated') {
        const isValidated = event.newValue === 'true';
        
        if (isValidated) {
          this.hidePinModal();
        }
      }
    });
    
    window.addEventListener('pinValidationChanged', ((event: CustomEvent) => {
      const isValidated = event.detail.validated;
      
      if (isValidated) {
        this.hidePinModal();
      }
    }) as EventListener);
  }

  showPinModal(): void {
    this.showPinModalSubject.next(true);
  }

  isPinModalVisible(): boolean {
    return this.showPinModalSubject.value;
  }

  hidePinModal(): void {
    this.showPinModalSubject.next(false);
  }

  notifyPinValidated(): void {
    this.pinValidatedSubject.next(true);
    
    // Esperar un momento antes de cerrar el modal
    // Esto previene problemas de navegación
    setTimeout(() => {
      this.hidePinModal();
    }, 300);
    
    setTimeout(() => {
      this.pinValidatedSubject.next(false);
    }, 1000);
  }

  resetState(): void {
    this.hidePinModal();
    this.pinValidatedSubject.next(false);
    this.resetFailedAttempts();
    this.resetPinSentFlag();
    localStorage.removeItem(this.FAILED_ATTEMPTS_KEY);
  }

  wasPinSentInCurrentSession(): boolean {
    return this.pinSentInCurrentSession;
  }

  markPinAsSent(): void {
    this.pinSentInCurrentSession = true;
  }

  resetPinSentFlag(): void {
    this.pinSentInCurrentSession = false;
  }

  getFailedAttempts(): number {
    return this.failedAttempts;
  }

  incrementFailedAttempts(): void {
    this.failedAttempts++;
    this.saveFailedAttempts();
  }

  resetFailedAttempts(): void {
    this.failedAttempts = 0;
    this.saveFailedAttempts();
  }

  hasReachedMaxAttempts(): boolean {
    return this.failedAttempts >= this.MAX_ATTEMPTS;
  }

  getMaxAttempts(): number {
    return this.MAX_ATTEMPTS;
  }

  private saveFailedAttempts(): void {
    localStorage.setItem(this.FAILED_ATTEMPTS_KEY, this.failedAttempts.toString());
  }

  private loadFailedAttempts(): void {
    const savedAttempts = localStorage.getItem(this.FAILED_ATTEMPTS_KEY);
    if (savedAttempts) {
      this.failedAttempts = parseInt(savedAttempts, 10);
    }
  }
}
